import { Component, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Link } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import Nav from "@/components/Nav";
import Home from "@/pages/Home";
import Work from "@/pages/Work";
import CaseStudyPage from "@/pages/CaseStudy";

class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { error: null };
    }
    static getDerivedStateFromError(error) {
        return { error };
    }
    render() {
        if (this.state.error) {
            return (
                <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-ink px-6 text-center">
                    <h1 className="font-display text-4xl text-paper">Something cracked.</h1>
                    <p className="font-mono text-xs uppercase tracking-[0.2em] text-smoke">
                        {String(this.state.error?.message || "Unknown error")}
                    </p>
                    <a href="/" className="bg-vermilion px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] text-paper">
                        Reload
                    </a>
                </div>
            );
        }
        return this.props.children;
    }
}

const ScrollToTop = () => {
    const { pathname } = useLocation();
    useEffect(() => {
        if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
        else window.scrollTo(0, 0);
    }, [pathname]);
    return null;
};

const NotFound = () => (
    <main className="flex min-h-screen flex-col items-center justify-center gap-8 px-6 text-center">
        <p className="eyebrow">404</p>
        <h1 className="font-display text-6xl font-light text-paper">
            Lost the signal<span className="text-vermilion">.</span>
        </h1>
        <Link
            to="/"
            data-testid="notfound-home-link"
            className="border border-line px-6 py-4 font-mono text-xs uppercase tracking-[0.2em] text-paper transition-colors duration-300 hover:border-vermilion hover:text-vermilion"
        >
            Back home
        </Link>
    </main>
);

function App() {
    useEffect(() => {
        const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
        window.__lenis = lenis;
        let raf;
        const loop = (t) => {
            lenis.raf(t);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(raf);
            lenis.destroy();
            window.__lenis = null;
        };
    }, []);

    return (
        <ErrorBoundary>
            <BrowserRouter>
                <ScrollToTop />
                <Nav />
                <div className="noise-overlay" aria-hidden="true" />
                <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/work" element={<Work />} />
                    <Route path="/work/:slug" element={<CaseStudyPage />} />
                    <Route path="*" element={<NotFound />} />
                </Routes>
                <Toaster position="bottom-left" theme="dark" />
            </BrowserRouter>
        </ErrorBoundary>
    );
}

export default App;
